import addressesApiFactory, { IAddressesApi } from './addresses';
import adminLoginApiFactory, { IAdminLoginApi } from './adminLogin';
import adminStateMachineApiFactory, { IAdminStateMachineApi } from './adminStateMachine';
import authenticationApiFactory, { IAuthenticationApi } from './authentication';
import availabilityApiFactory, { IAvailabilityApi } from './availability';
import availabilityV3ApiFactory, { IAvailabilityV3Api } from './availabilityV3';
import bookingPaymentApiFactory, { IBookingPaymentApi } from './bookingPayment';
import BookingsApiFactory, { IBookingsApi } from './bookings';
import brokerApiFactory, { IBrokerApi } from './broker';
import calendarSourceApiFactory, { ICalendarSourceApi } from './calendarSource';
import churnApiFactory, { IChurnApi } from './churn';
import clientApiFactory, { IClientApi } from './client';
import commissionServiceApiFactory, { ICommissionApi } from './commission';
import contractApiFactory, { IContractApi } from './contracts';
import eventNotesApiFactory, { IEventNotesApi } from './eventNotes';
import fxApiFactory, { IFXApi } from './fx';
import googleApiFactory, { IGoogleMapsApi } from './googlemapsapi';
import hostPaymentsApiFactory, { IHostPaymentsApi } from './hostPayments';
import { configureHttp } from './http';
import icalValidationApiFactory, { IIcalValidationApi } from './icalValidation';
import imagesApiFactory, { IImagesApi } from './images';
import importerApiFactory, { IImporterApi } from './importer';
import legacyLookupFactory, { ILegacyLookupApi } from './legacyLookup';
import listingsApiFactory, { IListingsApi } from './listings';
import listingsV2ApiFactory, { IListingsV2Api } from './listingsV2';
import marketingPreferencesApiFactory, { IMarketingPreferencesApi } from './marketingPreferences';
import notificationsApiFactory, { INotificationsApi } from './notifications';
import payLaterApiFactory, { IPayLaterAPI } from './payLater';
import postPaymentsApiFactory, { IPostPaymentsApi } from './postPayments';
import pricingApiFactory, { IPricingApi } from './pricing';
import propertyDescriptionApiFactory, { IPropertyDescriptionApi } from './propertyDescription';
import propertyVerificationApiFactory, { IPropertyVerificationApi } from './propertyVerification';
import qualityApiFactory, { IPropertyQualityApi } from './quality';
import rentalsunitedCurrencyApiFactory, { IRentalsunitedCurrencyApi } from './rentalsunitedCurrency';
import sessionsApiFactory, { ISessionsApi } from './sessions';

const authInterceptor = async (config: any) => {
	const user = await api.authentication.getCurrentUser();
	const session = user ? user.getSignInUserSession() : null;
	const jwt = session ? session.getIdToken().getJwtToken() : '';
	config.headers = {
		'Authorization': `Bearer ${jwt}`,
		...config.headers
	};
	return config;
};

export const http = configureHttp();
http.setBaseUrl(process.env.API_URL || '//API_URL_ENV_UNSET_ERROR');
http.addRequestInterceptor(authInterceptor);

const httpApiGateway = configureHttp();
httpApiGateway.setBaseUrl(process.env.AWS_APIG_URL || '//AWS_APIG_URL_ENV_UNSET_ERROR');
httpApiGateway.addRequestInterceptor(authInterceptor);

const httpGoogleMaps = configureHttp();
httpGoogleMaps.setBaseUrl(process.env.GOOGLE_MAPS_API_URL || 'https://maps.googleapis.com/maps/api/geocode/');

const httpAddresses = configureHttp();
httpAddresses.setBaseUrl('https://api.getAddress.io');

const httpAnonymous = configureHttp();
httpAnonymous.setBaseUrl(process.env.API_URL || '//API_URL_ENV_UNSET_ERROR');

const httpHostPayments = configureHttp();
httpHostPayments.setBaseUrl(process.env.PAYMENTS_GATEWAY_URI || 'PAYMENTS_APIG_URL_ENV_UNSET_ERROR');
httpHostPayments.addRequestInterceptor(authInterceptor);
export interface IApi {
	addresses: IAddressesApi;
	adminLogin: IAdminLoginApi;
	adminStateMachine: IAdminStateMachineApi;
	authentication: IAuthenticationApi;
	availability: IAvailabilityApi;
	availabilityV3: IAvailabilityV3Api;
	bookings: IBookingsApi;
	bookingPayment: IBookingPaymentApi;
	broker: IBrokerApi;
	contracts: IContractApi;
	calendarSource: ICalendarSourceApi;
	client: IClientApi;
	commission: ICommissionApi;
	churn: IChurnApi;
	eventNotes: IEventNotesApi;
	fx: IFXApi;
	googlemaps: IGoogleMapsApi;
	hostPayments: IHostPaymentsApi;
	images: IImagesApi;
	icalValidaton: IIcalValidationApi;
	importer: IImporterApi;
	legacyLookup: ILegacyLookupApi;
	listings: IListingsApi;
	listingsV2: IListingsV2Api;
	notifications: INotificationsApi;
	postPayments: IPostPaymentsApi;
	pricing: IPricingApi;
	propertyDescription: IPropertyDescriptionApi;
	quality: IPropertyQualityApi;
	rentalsunitedCurrency: IRentalsunitedCurrencyApi;
	sessions: ISessionsApi;
	paylater: IPayLaterAPI;
	marketingPreferences: IMarketingPreferencesApi;
	propertyVerification: IPropertyVerificationApi;
}

const apiFactory = (): IApi => ({
	addresses: addressesApiFactory(httpAddresses),
	adminLogin: adminLoginApiFactory(http, '/client'),
	adminStateMachine: adminStateMachineApiFactory(httpApiGateway),
	authentication: authenticationApiFactory(),
	availability: availabilityApiFactory(http, '/availability'),
	availabilityV3: availabilityV3ApiFactory(http, '/availability-v3'),
	bookings: BookingsApiFactory(http, '/availability-v3'),
	bookingPayment: bookingPaymentApiFactory(http, '/booking_payment'),
	broker: brokerApiFactory(http, '/broker'),
	contracts: contractApiFactory(http, '/contracts'),
	calendarSource: calendarSourceApiFactory(http, '/calendar_source'),
	commission: commissionServiceApiFactory(http, '/commission'),
	churn: churnApiFactory(http, '/customers'),
	client: clientApiFactory(http, '/client'),
	eventNotes: eventNotesApiFactory(http, '/availability-v3'),
	fx: fxApiFactory(http, '/fx'),
	googlemaps: googleApiFactory(httpGoogleMaps),
	hostPayments: hostPaymentsApiFactory(httpHostPayments),
	images: imagesApiFactory(http, '/images'),
	icalValidaton: icalValidationApiFactory(http, '/ical_validator'),
	importer: importerApiFactory(http, '/import'),
	legacyLookup: legacyLookupFactory(http, '/client'),
	listings: listingsApiFactory(http, '/listings'),
	listingsV2: listingsV2ApiFactory(http, '/listings-v2'),
	notifications: notificationsApiFactory(http, '/notifications'),
	postPayments: postPaymentsApiFactory(http, '/availability/v2'),
	pricing: pricingApiFactory(http, '/pricing'),
	propertyDescription: propertyDescriptionApiFactory(http, '/property-description'),
	quality: qualityApiFactory(http, '/quality'),
	rentalsunitedCurrency: rentalsunitedCurrencyApiFactory(http, '/rentalsunited_currency'),
	sessions: sessionsApiFactory(httpAnonymous),
	paylater: payLaterApiFactory(httpAnonymous),
	marketingPreferences: marketingPreferencesApiFactory(http, '/marketing-preferences'),
	propertyVerification: propertyVerificationApiFactory(http)
});

const api = apiFactory();

export default api;
